import type { RefObject } from 'react'
import { useEffect, useRef } from 'react'

interface PointerParallax<T extends HTMLElement> {
  ref: RefObject<T>
}

/**
 * Publishes the pointer's offset from the centre of the viewport as `--px`
 * and `--py` (-1..1) on the element, for layers to parallax against.
 *
 * Decorative mouse-tracking, which belongs on a marketing surface and
 * nowhere near content the visitor is reading. Disabled entirely for coarse
 * pointers and reduced motion — there is no hover on touch, and the whole
 * effect is movement.
 */
export const usePointerParallax = <T extends HTMLElement = HTMLElement>(): PointerParallax<T> => {
  const ref = useRef<T>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const fine = window.matchMedia('(hover: hover) and (pointer: fine)')
    const still = window.matchMedia('(prefers-reduced-motion: reduce)')
    if (!fine.matches || still.matches) return

    let frame = 0
    let x = 0
    let y = 0

    const apply = () => {
      frame = 0
      el.style.setProperty('--px', x.toFixed(3))
      el.style.setProperty('--py', y.toFixed(3))
    }

    const onMove = (event: PointerEvent) => {
      x = (event.clientX / window.innerWidth) * 2 - 1
      y = (event.clientY / window.innerHeight) * 2 - 1
      if (!frame) frame = requestAnimationFrame(apply)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('pointermove', onMove)
    }
  }, [])

  return { ref }
}
